import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  FiBell,
  FiCalendar,
  FiUser,
  FiCreditCard,
  FiCheckCircle,
  FiX
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/DoctorNotifications.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

// Map backend "type" values to an icon.
// Falls back to a generic bell icon for any type not listed here.
const TYPE_ICON_MAP = {
  appointment: <FiCalendar />,
  patient: <FiUser />,
  payment: <FiCreditCard />,
};

// Lightweight relative-time formatter so we don't need an extra library.
const timeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const seconds = Math.floor((new Date() - date) / 1000);

  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
};

const DoctorNotifications = () => {
  const { baseUrl, logout } = useContext(AppContext);
  const token = localStorage.getItem("therapist_token");
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/Notifications/list`, authHeaders);
        const data = res.data?.data ?? res.data ?? [];
        const list = (Array.isArray(data) ? data : []).map((n) => ({
          id: n.id,
          type: n.type || "general",
          title: n.title,
          description: n.description || n.message || "",
          time: timeAgo(n.created_at),
          unread: !n.read_at && !n.is_read,
        }));
        setNotifications(list);
      } catch (err) {
        console.log("Notifications fetch error:", err);
        if (err?.response?.status === 401) {
          logout();
        } else {
          setError("Could not load notifications.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [baseUrl, token]);

  const unreadCount = notifications.filter((n) => n.unread).length;

  const markAsRead = async (id) => {
    // Optimistic update — flip it locally first
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === id ? { ...notif, unread: false } : notif))
    );

    try {
      await axios.post(`${baseUrl}/doctor/Notifications/${id}/read`, {}, authHeaders);
    } catch (err) {
      console.log("Mark as read error:", err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        // Roll back on failure
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === id ? { ...notif, unread: true } : notif))
        );
      }
    }
  };

  const markAllRead = async () => {
    const previous = notifications;
    setMarkingAllRead(true);
    setNotifications((prev) => prev.map((notif) => ({ ...notif, unread: false })));

    try {
      await axios.post(`${baseUrl}/doctor/Notifications/read-all`, {}, authHeaders);
    } catch (err) {
      console.log("Mark all as read error:", err);
      setNotifications(previous); // rollback
      if (err?.response?.status === 401) logout();
    } finally {
      setMarkingAllRead(false);
    }
  };

  const dismissNotification = async (e, id) => {
    e.stopPropagation();

    const previous = notifications;
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));

    try {
      await axios.delete(`${baseUrl}/doctor/Notifications/${id}`, authHeaders);
    } catch (err) {
      console.log("Dismiss notification error:", err);
      setNotifications(previous); // rollback on failure
      if (err?.response?.status === 401) logout();
    }
  };

  return (
    <DoctorMasterLayout>
      <div className="dn-body">
        <div className="dn-container">
          {/* Header */}
          <div className="dn-header">
            <div className="dn-header-left">
              <h2 className="dn-title">Notifications</h2>
              <p className="dn-subtitle">System updates and important alerts</p>
            </div>

            {unreadCount > 0 && (
              <button className="dn-btn-mark" onClick={markAllRead} disabled={markingAllRead}>
                <FiCheckCircle /> {markingAllRead ? "Marking…" : `Mark all as read (${unreadCount})`}
              </button>
            )}
          </div>

          {/* List Section */}
          <div className="dn-list-section">
            {loading && <div className="dn-no-data">Loading notifications…</div>}

            {!loading && error && <div className="dn-error">{error}</div>}

            {!loading && !error && notifications.length === 0 && (
              <div className="dn-no-data">
                <FiBell />
                You're all caught up — no notifications yet.
              </div>
            )}

            {!loading && !error && notifications.length > 0 && (
              <div className="dn-grid">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`dn-item${notification.unread ? " dn-item-unread" : ""}`}
                    onClick={() => notification.unread && markAsRead(notification.id)}
                  >
                    <div className={`dn-icon dn-icon-${notification.type}`}>
                      {TYPE_ICON_MAP[notification.type] || <FiBell />}
                    </div>
                    <div className="dn-content">
                      <div className="dn-item-header">
                        <h4 className="dn-item-title">{notification.title}</h4>
                        <span className="dn-badge">{notification.type}</span>
                      </div>
                      <p className="dn-desc">{notification.description}</p>
                      <div className="dn-item-footer">
                        <span className="dn-time">{notification.time}</span>
                        {notification.unread && (
                          <button
                            className="dn-dismiss-btn"
                            onClick={(e) => dismissNotification(e, notification.id)}
                          >
                            <FiX /> Dismiss
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default DoctorNotifications;